import { Box } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthProvider";
import { useHospitalData } from "../../../contexts/HospitalDataProvider";
import { InlineHint } from "../../UIToolkit/molecules/InlineHint";
import SystemButton from "../../UIToolkit/SystemButton/SystemButton";
import { BreakRulesSection } from "./BreakRulesSection";
import type { WageSettingsTarget, WageSettingsTargetSection } from "./deepLink";
import { HoursRateSection } from "./HoursRateSection";
import { OvertimeSection } from "./OvertimeSection";
import { TrainingOrientationSection } from "./TrainingOrientationSection";
import type { WageRuleDeepLink } from "./useWageRuleDeepLink";
import { useWageSettings } from "./useWageSettings";

type Props = {
	/** Where a deep link (the wage breakdown's rate type) asked to land. `undefined` = open at the top. */
	target: WageSettingsTarget | undefined;
};

/** The deep link for one section: the target's rule when it points here, otherwise nothing. */
const deepLinkFor = (target: WageSettingsTarget | undefined, section: WageSettingsTargetSection): WageRuleDeepLink =>
	target?.section === section ? target : undefined;

/**
 * Team → Wage Settings: every department wage rule a facility admin can change themselves.
 *
 * DAT-783 moved the training and orientation wages here; DAT-905 moved the Hours rate, Overtime and
 * Break rules tables after them, all out of Settings → Department Configuration, which is registered
 * `access: SUPPORT`. Each is the ONLY edit surface for its values, and each section saves on its own
 * endpoint with its own Save, so saving one never resends another's rows.
 *
 * Every section's stored values come from ONE read for the department in the URL (`useWageSettings`),
 * not from `chosenDepartment`, which is fully loaded only for the token's department.
 *
 * Mounted with a per-department `key` by the Team page, so switching departments re-seeds every
 * section's draft from freshly-mounted state.
 */
export const WageSettings: FC<Props> = ({ target }) => {
	const { t } = useTranslation();
	const { chosenDepartment } = useHospitalData();
	const { userInfo } = useAuth();
	const { data: settings, isError, refetch, isFetching } = useWageSettings(chosenDepartment.department_id);

	// The per-department permission record, not the account-wide flag: a user can administer one
	// building and only watch another. The backend guard refuses a read-only write too; this keeps a
	// view-only admin from reaching a 403 snackbar instead of a disabled button.
	const currentDept = userInfo?.departments?.find((dept) => dept.department_id === chosenDepartment.department_id);
	const isReadOnly = currentDept?.is_readonly === true;

	return (
		<Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 4, pb: 4 }}>
			{/* Every section stays locked while there is no read, so a failure must say so rather than leave
			    a page of disabled controls that look like they are still loading. */}
			{isError && settings === undefined && (
				<InlineHint
					tone="warning"
					dataTestId="team-wage-settings-load-error"
					sx={{ width: "100%" }}
					action={
						<SystemButton
							buttonVariant="borderless"
							size="small"
							onClick={() => refetch()}
							loading={isFetching}
							noFullWidthMobile
							data-testid="team-wage-settings-load-retry"
						>
							{t("retry")}
						</SystemButton>
					}
				>
					{t("team.wageSettings.loadError")}
				</InlineHint>
			)}
			<TrainingOrientationSection settings={settings} isReadOnly={isReadOnly} deepLink={deepLinkFor(target, "training-orientation")} />
			<HoursRateSection settings={settings} isReadOnly={isReadOnly} deepLink={deepLinkFor(target, "hours-rate")} />
			<OvertimeSection settings={settings} isReadOnly={isReadOnly} deepLink={deepLinkFor(target, "overtime")} />
			<BreakRulesSection settings={settings} isReadOnly={isReadOnly} deepLink={deepLinkFor(target, "break-rules")} />
		</Box>
	);
};
